import { Injectable, inject } from '@angular/core';
import {
    HttpInterceptor,
    HttpRequest,
    HttpHandler,
    HttpEvent,
    HttpResponse,
    HttpErrorResponse,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import {
    AdminTestApiArea,
    AdminTestApiService,
} from './admin-test-api.service';

/**
 * HTTP Interceptor for Admin Test API
 *
 * Compares incoming requests against regex patterns per active TestArea
 * and stores request/response pairs on match in the signal
 */
@Injectable()
export class AdminTestApiHttpInterceptor implements HttpInterceptor {
    private readonly testApiService = inject(AdminTestApiService);

    /**
     * Configuration of regex patterns for each TestArea
     * The patterns are used to filter incoming requests
     */
    ADMIN_TEST_API_PATTERNS: Record<AdminTestApiArea, RegExp[]> = {
        [AdminTestApiArea.WA_ARTICLE]: [
            // WA Article API Patterns
            /\/api\/v3\/organization-sites\/\d*\/consignor\/delivery-note-bundles/i,
            /\/api\/v2\/organization-sites\/\d*\/consignor\/delivery-note-bundles\/\d*/i,
            /\/api\/v2\/organization-sites\/\d*\/consignor\/delivery-notes\/\d*\/articles/i,
            /\/api\/v2\/organization-sites\/\d*\/delivery-notes\/\d*\/articles/i,
            /\/api\/v3\/organization-sites\/\d*\/consignor\/delivery-notes\/\d*\/article-notes/i,
            /\/api\/v3\/organization-sites\/\d*\/consignor\/delivery-notes\/\d*\/articles\/\d*\/notes/i,
        ],
        [AdminTestApiArea.ARTICLE_COMPARE]: [
            // Article Compare API Patterns
            /\/api\/v3\/organization-sites\/\d*\/consignee\/articles-compare/i,
        ],
        [AdminTestApiArea.ASN]: [
            // ASN (Advanced Shipping Notice) API Patterns
            /\/api\/v3\/organization-sites\/\d*\/consignee\/advanced-shipping-notes/i,
        ],
        [AdminTestApiArea.DESADV]: [
            // DESADV (EDIFACT despatch advice) API Patterns
            /\/api\/v3\/organization-sites\/\d*\/consignor\/desadv/i,
        ],
    };

    intercept(
        request: HttpRequest<any>,
        next: HttpHandler
    ): Observable<HttpEvent<any>> {
        // If no TestAreas are active, pass the request through normally
        if (this.testApiService.activeTestAreas.size === 0) {
            return next.handle(request);
        }

        // Check if this request matches an active TestArea, if no match, pass the request through normally
        if (!this.shouldLogRequest(request)) {
            return next.handle(request);
        }

        const apiTestEvent = this.testApiService.createTestEvent(request);

        // Intercept the response using tap
        return next.handle(request).pipe(
            tap({
                next: (event: HttpEvent<any>) => {
                    // Only process HttpResponse events
                    if (event instanceof HttpResponse) {
                        apiTestEvent.response.set(event);
                    }
                },
                error: (error: HttpErrorResponse) => {
                    console.error(
                        'HTTP Error intercepted by AdminTestApiHttpInterceptor:',
                        error
                    );
                    apiTestEvent.responseError.set(error);
                },
            })
        );
    }

    /**
     * Checks if the request matches an active TestArea
     */
    private shouldLogRequest(request: HttpRequest<any>): boolean {
        const url = request.url.toLowerCase();

        // Iterate through each active TestArea
        for (const area of this.testApiService.activeTestAreas) {
            const patterns = this.ADMIN_TEST_API_PATTERNS[area];
            if (patterns && patterns.some(p => p.test(url))) {
                return true;
            }
        }

        return false;
    }
}
